/**
 * BomItemEditor — bir reçetenin (Bom) malzeme satırlarını yönetir.
 *
 * Reçete kartının içinde kullanılır:
 *  - Mevcut BomItem satırlarını tablo halinde gösterir (hammadde × miktar
 *    × birim maliyet × satır maliyeti)
 *  - Altta "Malzeme Ekle" satırı: hammadde seç + miktar gir → anında ekler
 *  - Her satır tek tıkla silinebilir
 *  - Toplam birim maliyet canlı hesaplanır
 *
 * Maliyet = Σ (quantity × ingredient.last_cost_minor). Backend cost-breakdown
 * tedarikçi fiyatına da bakar; burada hızlı önizleme için yalnız
 * last_cost_minor kullanılır (Recipe sayfası amacı için yeterli).
 */
import { useState } from "react";
import { ActionIcon, Button, Checkbox, Group, NumberInput, Select, Switch, Table, Text } from "@mantine/core";
import { IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useCreate, useRemove, useUpdate } from "../../../shared/api/resources";
import { MoneyDisplay } from "../../../shared/ui";
import { IngredientPickerModal } from "./IngredientPickerModal";
import { recipeUnitsFor, conversionFactor, defaultRecipeInputUnit } from "./goodsReceiptUnits";

interface BomItemRow {
  id: string;
  ingredient: string;
  ingredient_name: string;
  ingredient_unit: string;
  /** Hammaddenin birim maliyeti (kuruş) — serializer source=ingredient.last_cost_minor. */
  ingredient_cost_minor: number;
  quantity: string;
  is_optional?: boolean;
}

/** Picker'dan seçilen hammadde. */
interface PickedIngredient {
  id: string;
  name: string;
  unit: string;
  last_cost_minor: number;
}

interface Props {
  bomId: string;
  items: BomItemRow[];
}

/** "30.000" → "30", "1.500" → "1.5" — DecimalField'in fazla sıfırlarını at. */
function formatQty(q: string): string {
  const n = Number(q);
  return Number.isFinite(n) ? String(n) : q;
}

/**
 * Tabloda miktarı okunur birimle göster. quantity her zaman STOK birimi
 * cinsinden saklanır (kg). Küçük kesirli değerleri (ör. 0.25 kg) alt birimle
 * (250 gr) göstermek okunabilirliği artırır — "0.25 kg" yerine "250 gr".
 * Sadece 1'in altındaki kütle/hacim miktarlarında alt birime düşürür.
 */
function displayQty(q: string, unit: string): { value: string; unit: string } {
  const n = Number(q);
  if (!Number.isFinite(n)) return { value: q, unit };
  if (n > 0 && n < 1 && unit === "kg") return { value: formatQty(String(n * 1000)), unit: "gr" };
  if (n > 0 && n < 1 && unit === "lt") return { value: formatQty(String(n * 1000)), unit: "ml" };
  return { value: formatQty(String(n)), unit };
}

/**
 * Birim sayılabilir/tam mı (adet, porsiyon...) yoksa ölçülebilir/ondalıklı
 * mı (gr, ml, kg, lt). Adet birimlerinde miktar tam sayı olmalı — yarım
 * köfte sipariş edilemez.
 */
function isWholeUnit(unit: string | undefined): boolean {
  const u = (unit || "").toLowerCase().trim();
  return u === "adet" || u === "porsiyon" || u === "piece" || u === "portion";
}

export function BomItemEditor({ bomId, items }: Props) {
  const qc = useQueryClient();
  const createItem = useCreate("bom-items");
  const updateItem = useUpdate("bom-items");
  const removeItem = useRemove("bom-items");
  const [pickerOpen, pickerHandlers] = useDisclosure(false);

  // Eklenecek satır için seçili hammadde + miktar
  const [picked, setPicked] = useState<PickedIngredient | null>(null);
  const [quantity, setQuantity] = useState<number | string>(1);
  // Kullanıcının miktarı GİRDİĞİ birim (stok biriminden farklı olabilir:
  // hammadde kg, kullanıcı gr girer). Kayıtta stok birimine çevrilir.
  const [inputUnit, setInputUnit] = useState<string>("");
  const [newOptional, setNewOptional] = useState(false);

  // Seçili hammadde için girilebilir birim seçenekleri (kg → [gr, kg]).
  const unitOptions = picked ? recipeUnitsFor(picked.unit) : [];
  // Birden çok seçenek varsa Select göster; tek seçenek varsa düz etiket.
  const showUnitSelect = unitOptions.length > 1;

  // Satır maliyeti = miktar × hammaddenin birim maliyeti (satırda gömülü)
  const lineCost = (it: BomItemRow) =>
    Math.round(Number(it.quantity) * (it.ingredient_cost_minor || 0));

  const totalCost = items.reduce((sum, it) => sum + lineCost(it), 0);

  // Picker'da zaten reçetede olan hammaddeler gizlenir
  // (model `(bom, ingredient)` unique).
  const usedIngredientIds = items.map((it) => it.ingredient);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["boms"] });
    // Ürün maliyet/marj sütunu da bu reçeteye bağlı — tazele.
    qc.invalidateQueries({ queryKey: ["product-cost"] });
  }

  async function handleAdd() {
    if (!picked) {
      notifications.show({ message: "Hammadde seçin", color: "orange" });
      return;
    }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      notifications.show({ message: "Miktar 0'dan büyük olmalı", color: "orange" });
      return;
    }
    // Adet/porsiyon birimde kesirli miktar olmaz (savunma — UI zaten engeller).
    // Kontrol GİRİŞ birimine göre: gr seçiliyse ondalık serbest.
    const effInputUnit = inputUnit || picked.unit;
    if (isWholeUnit(effInputUnit) && !Number.isInteger(qty)) {
      notifications.show({
        message: `${effInputUnit} biriminde miktar tam sayı olmalı`,
        color: "orange",
      });
      return;
    }
    // Girilen miktarı hammaddenin STOK birimine çevir (örn. 250 gr → 0.250 kg).
    // BomItem.quantity her zaman stok birimi cinsinden tutulur.
    const factor = conversionFactor(picked.unit, effInputUnit);
    const stockQty = qty * factor;
    try {
      await createItem.mutateAsync({
        bom: bomId,
        ingredient: picked.id,
        quantity: stockQty,
        is_optional: newOptional,
      });
      setPicked(null);
      setQuantity(1);
      setInputUnit("");
      setNewOptional(false);
      refresh();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail
          || e?.response?.data?.non_field_errors?.[0]
          || "Malzeme eklenemedi",
        color: "red",
      });
    }
  }

  async function handleRemove(id: string) {
    try {
      await removeItem.mutateAsync(id);
      refresh();
    } catch {
      notifications.show({ message: "Malzeme silinemedi", color: "red" });
    }
  }

  return (
    <>
    <Table.ScrollContainer minWidth={460}>
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Malzeme</Table.Th>
          <Table.Th>Miktar</Table.Th>
          <Table.Th ta="right">Birim Maliyet</Table.Th>
          <Table.Th ta="right">Satır Maliyeti</Table.Th>
          <Table.Th w={44}></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.map((it) => (
          <Table.Tr key={it.id}>
            <Table.Td>
              <Text>{it.ingredient_name}</Text>
              <Switch
                size="xs"
                mt={4}
                label="Opsiyonel (POS'ta çıkarılabilir)"
                checked={!!it.is_optional}
                onChange={async (e) => {
                  try {
                    await updateItem.mutateAsync({
                      id: it.id,
                      is_optional: e.currentTarget.checked,
                    });
                    refresh();
                  } catch {
                    notifications.show({
                      message: "Güncellenemedi", color: "red",
                    });
                  }
                }}
              />
            </Table.Td>
            {/* Miktar + birim tek hücrede — "250 gr" gibi okunur. Küçük
                kesirli kg/lt değerleri alt birime düşürülür (displayQty). */}
            <Table.Td>
              {(() => {
                const d = displayQty(it.quantity, it.ingredient_unit);
                return (
                  <>
                    <Text span fw={600}>{d.value}</Text>
                    <Text span c="dimmed"> {d.unit}</Text>
                  </>
                );
              })()}
            </Table.Td>
            <Table.Td ta="right" c="dimmed">
              {/* Birim maliyet hammaddenin ölçü birimi başınadır */}
              <MoneyDisplay minor={it.ingredient_cost_minor || 0} />
              <Text span size="xs" c="dimmed"> / {it.ingredient_unit}</Text>
            </Table.Td>
            <Table.Td ta="right" fw={600}>
              {it.ingredient_cost_minor > 0
                ? <MoneyDisplay minor={lineCost(it)} />
                : <Text size="xs" c="orange">maliyet yok</Text>}
            </Table.Td>
            <Table.Td>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => handleRemove(it.id)}
                loading={removeItem.isPending}
                aria-label="Malzemeyi sil"
              >
                <IconTrash size={15} />
              </ActionIcon>
            </Table.Td>
          </Table.Tr>
        ))}

        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={5} ta="center" c="dimmed">
              Henüz malzeme eklenmemiş
            </Table.Td>
          </Table.Tr>
        )}

        {/* Toplam satırı */}
        {items.length > 0 && (
          <Table.Tr>
            <Table.Td colSpan={3} ta="right" fw={700}>Birim Maliyet (toplam)</Table.Td>
            <Table.Td ta="right" fw={800} c="teal">
              <MoneyDisplay minor={totalCost} />
            </Table.Td>
            <Table.Td />
          </Table.Tr>
        )}

        {/* Malzeme ekleme satırı */}
        <Table.Tr>
          <Table.Td colSpan={2}>
            {/* Hammadde, server-side aranan modaldan seçilir — düz Select
                binlerce hammaddede DOM'u şişirir. */}
            <Button
              variant="light"
              size="xs"
              leftSection={<IconSearch size={14} />}
              onClick={pickerHandlers.open}
              fullWidth
              justify="flex-start"
            >
              {picked ? `${picked.name} (${picked.unit})` : "Hammadde seç..."}
            </Button>
            <Checkbox
              size="xs"
              mt={6}
              label="Opsiyonel — POS'ta çıkarılabilir"
              checked={newOptional}
              onChange={(e) => setNewOptional(e.currentTarget.checked)}
              disabled={!picked}
            />
          </Table.Td>
          <Table.Td>
            {/* Miktar + giriş birimi. Adet/porsiyon birimlerinde miktar tam
                sayı — yarım köfte olmaz. Ölçü birimlerinde (gr/ml) ondalığa
                izin ver. Hammadde kg ise kullanıcı gr seçip "250" girebilir;
                kayıtta 0.250 kg'a çevrilir. */}
            <Group gap={4} wrap="nowrap" align="flex-start">
              <NumberInput
                placeholder="Miktar"
                value={quantity}
                onChange={setQuantity}
                min={0}
                step={isWholeUnit(inputUnit || picked?.unit) ? 1 : 0.1}
                decimalScale={isWholeUnit(inputUnit || picked?.unit) ? 0 : 3}
                allowDecimal={!isWholeUnit(inputUnit || picked?.unit)}
                size="xs"
                disabled={!picked}
                style={{ flex: 1, minWidth: 0 }}
              />
              {picked && showUnitSelect ? (
                <Select
                  size="xs"
                  w={68}
                  value={inputUnit || picked.unit}
                  onChange={(v) => v && setInputUnit(v)}
                  data={unitOptions}
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true }}
                />
              ) : (
                <Text size="xs" c="dimmed" mt={6} style={{ whiteSpace: "nowrap" }}>
                  {picked?.unit}
                </Text>
              )}
            </Group>
          </Table.Td>
          <Table.Td colSpan={2}>
            <Group justify="flex-end">
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={handleAdd}
                loading={createItem.isPending}
                disabled={!picked}
              >
                Malzeme Ekle
              </Button>
            </Group>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
    </Table.ScrollContainer>

    <IngredientPickerModal
      opened={pickerOpen}
      onClose={pickerHandlers.close}
      // Hammadde değişince miktarı 1'e sıfırla — önceki birime göre girilmiş
      // ondalık bir değer (ör. 1.5) yeni "adet" birimiyle uyumsuz olabilir.
      // Giriş birimini akıllı varsayılana çek: stok kg/lt ise gr/ml öner
      // (reçeteler çoğunlukla küçük miktar kullanır).
      onSelect={(ing) => {
        setPicked(ing);
        setQuantity(1);
        setInputUnit(defaultRecipeInputUnit(ing.unit));
      }}
      excludeIds={usedIngredientIds}
    />
    </>
  );
}
